import { CheckIcon, Tag } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { samlProviderPath } from "./constants";
import { IdentityProviderCard } from "./IdentityProviderCard";

import type { SamlProviderCard_provider$key } from "./__generated__/SamlProviderCard_provider.graphql";

/** Props for the SamlProviderCard component */
interface Props {
  /** Fragment reference for the SAML provider the card describes */
  providerRef: SamlProviderCard_provider$key;
}

/**
 * A configured SAML provider, listed as a card.
 *
 * Every SAML provider federates console logins, so the "SSO Login" capability
 * is always present; "Single Sign-Out" reflects the provider's `enableSignout`
 * flag and is omitted when the Cognito signout flow is off.
 */
export function SamlProviderCard({ providerRef }: Props) {
  const provider = useFragment(
    graphql`
      fragment SamlProviderCard_provider on SAMLProvider {
        displayName
        enableSignout
        idpNamespace
        idpType
        name
      }
    `,
    providerRef,
  );

  return (
    <IdentityProviderCard
      detailsPath={samlProviderPath(provider.name)}
      identifier={provider.name}
      idpType={provider.idpType}
      namespace={provider.idpNamespace}
      tags={[
        <Tag
          color="green"
          icon={<CheckIcon size={14} />}
          id="sso-login"
          key="sso-login"
        >
          SSO Login
        </Tag>,
        provider.enableSignout ? (
          <Tag
            color="green"
            icon={<CheckIcon size={14} />}
            id="signout"
            key="signout"
          >
            Single Sign-Out
          </Tag>
        ) : null,
      ]}
      title={provider.displayName || provider.name}
    />
  );
}
